/**
 * Analytics Data Service
 * Handles localStorage operations for visual analytics chart data
 * Each chart type stores data separately, scoped by user ID
 * Includes background cloud sync for cross-device persistence
 */

import { analyticsApiService } from './analyticsApiService';

class AnalyticsDataService {
  constructor() {
    this.storageKeyPrefix = 'chartara_analytics_';
    this.currentUserId = null;
    this.syncInProgress = new Set();
  }

  /**
   * Set the current user ID for data isolation
   */
  setCurrentUser(userId) {
    this.currentUserId = userId;
  }

  /**
   * Generate storage key for a specific chart type
   */
  getStorageKey(chartType) {
    if (!this.currentUserId) {
      console.warn('No user ID set for analytics data service');
      return `${this.storageKeyPrefix}anonymous_${chartType}`;
    }
    return `${this.storageKeyPrefix}${this.currentUserId}_${chartType}`;
  }

  /**
   * Save data for a specific chart type
   * @param {string} chartType - The chart identifier (e.g., 'treatment-plan-review')
   * @param {Array} data - Array of data records
   * @returns {object} - Save result with timestamp and record count
   */
  saveChartData(chartType, data) {
    try {
      const key = this.getStorageKey(chartType);
      const payload = {
        data: data,
        timestamp: new Date().toISOString(),
        recordCount: data.length,
        chartType: chartType
      };
      localStorage.setItem(key, JSON.stringify(payload));

      // Fire-and-forget cloud push
      this._pushToCloud(chartType, data);

      return {
        success: true,
        timestamp: payload.timestamp,
        recordCount: payload.recordCount
      };
    } catch (error) {
      console.error(`Error saving ${chartType} data:`, error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get data for a specific chart type
   * @param {string} chartType - The chart identifier
   * @returns {object|null} - The stored data with metadata, or null if not found
   */
  getChartData(chartType) {
    try {
      const key = this.getStorageKey(chartType);
      const stored = localStorage.getItem(key);
      if (!stored) {
        return null;
      }
      return JSON.parse(stored);
    } catch (error) {
      console.error(`Error loading ${chartType} data:`, error);
      return null;
    }
  }

  /**
   * Check if data exists for a specific chart type
   * @param {string} chartType - The chart identifier
   * @returns {boolean}
   */
  hasChartData(chartType) {
    const key = this.getStorageKey(chartType);
    return localStorage.getItem(key) !== null;
  }

  /**
   * Get metadata for a specific chart type (without loading full data)
   * @param {string} chartType - The chart identifier
   * @returns {object|null} - Metadata (timestamp, recordCount) or null
   */
  getChartMetadata(chartType) {
    const stored = this.getChartData(chartType);
    if (!stored) {
      return null;
    }
    return {
      timestamp: stored.timestamp,
      recordCount: stored.recordCount,
      chartType: stored.chartType
    };
  }

  /**
   * Clear data for a specific chart type
   * @param {string} chartType - The chart identifier
   */
  clearChartData(chartType) {
    try {
      const key = this.getStorageKey(chartType);
      localStorage.removeItem(key);

      // Fire-and-forget cloud delete
      this._deleteFromCloud(chartType);

      return { success: true };
    } catch (error) {
      console.error(`Error clearing ${chartType} data:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Clear all analytics data for current user
   */
  clearAllData() {
    try {
      const prefix = `${this.storageKeyPrefix}${this.currentUserId}_`;
      const keysToRemove = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key));
      return { success: true, clearedCount: keysToRemove.length };
    } catch (error) {
      console.error('Error clearing all analytics data:', error);
      return { success: false, error: error.message };
    }
  }

  // ===== Cloud Sync Methods =====

  /**
   * Push chart data to cloud (fire-and-forget, never blocks UI)
   */
  async _pushToCloud(chartType, data) {
    if (!this.currentUserId) return;
    const key = `push_${chartType}`;
    if (this.syncInProgress.has(key)) return;
    this.syncInProgress.add(key);
    try {
      await analyticsApiService.saveChart(chartType, data);
    } catch (error) {
      console.warn(`Cloud push failed for ${chartType}:`, error);
    } finally {
      this.syncInProgress.delete(key);
    }
  }

  /**
   * Delete chart data from cloud (fire-and-forget)
   */
  async _deleteFromCloud(chartType) {
    if (!this.currentUserId) return;
    try {
      await analyticsApiService.deleteChart(chartType);
    } catch (error) {
      console.warn(`Cloud delete failed for ${chartType}:`, error);
    }
  }

  /**
   * Sync a single chart type from cloud
   * Returns the cloud data if it was newer/missing locally, otherwise null
   */
  async syncFromCloud(chartType) {
    if (!this.currentUserId) return null;
    const key = `pull_${chartType}`;
    if (this.syncInProgress.has(key)) return null;
    this.syncInProgress.add(key);

    try {
      const cloudData = await analyticsApiService.fetchChart(chartType);
      const localData = this.getChartData(chartType);

      // Cloud has nothing
      if (!cloudData) {
        // If local has data, migrate it to cloud
        if (localData) {
          this._pushToCloud(chartType, localData.data);
        }
        return null;
      }

      // Local is empty — new device scenario
      if (!localData) {
        const payload = {
          data: cloudData.data,
          timestamp: cloudData.timestamp,
          recordCount: cloudData.recordCount,
          chartType: cloudData.chartType || chartType
        };
        const storageKey = this.getStorageKey(chartType);
        localStorage.setItem(storageKey, JSON.stringify(payload));
        return payload;
      }

      // Both exist — compare timestamps
      const cloudTime = new Date(cloudData.timestamp).getTime();
      const localTime = new Date(localData.timestamp).getTime();

      if (cloudTime > localTime) {
        // Cloud is newer — overwrite local
        const payload = {
          data: cloudData.data,
          timestamp: cloudData.timestamp,
          recordCount: cloudData.recordCount,
          chartType: cloudData.chartType || chartType
        };
        const storageKey = this.getStorageKey(chartType);
        localStorage.setItem(storageKey, JSON.stringify(payload));
        return payload;
      }

      if (localTime > cloudTime) {
        // Local is newer — push to cloud
        this._pushToCloud(chartType, localData.data);
      }

      return null;
    } catch (error) {
      console.warn(`Cloud sync failed for ${chartType}:`, error);
      return null;
    } finally {
      this.syncInProgress.delete(key);
    }
  }
}

// Export singleton instance
export const analyticsDataService = new AnalyticsDataService();
